import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { ensureIdentity, useIdentity } from '../../../features/auth/useAuth';
import { ResumeSessionBanner } from '../../../features/session/ResumeSessionBanner';
import { AppLayout } from '../../../layouts/AppLayout';

/**
 * The player area. Spec 12.1: `/app` requires an account.
 *
 * Spec 10.8: "The frontend hides controls the user cannot use. That is cosmetic and is
 * never the enforcement point." This guard exists to route the user somewhere useful, not
 * to protect data — every procedure behind it re-checks on the server.
 */
export const Route = createFileRoute('/$lang/app')({
  beforeLoad: async ({ context, params, location }) => {
    const identity = await ensureIdentity(context.queryClient);

    if (identity === null) {
      /* Spec 22.3: UNAUTHENTICATED redirects to login, preserving where they were going. */
      throw redirect({
        to: '/$lang/login',
        params: { lang: params.lang },
        search: { returnTo: location.pathname },
      });
    }

    /*
     * A guest is authenticated but has no player area (spec 10.5). Sending them to login
     * rather than to a 403 is deliberate: what they need is an account, and that is where
     * they get one.
     */
    if (identity.isGuest) {
      throw redirect({
        to: '/$lang/signup',
        params: { lang: params.lang },
      });
    }
  },
  component: AppArea,
});

function AppArea(): React.ReactNode {
  const { lang } = Route.useParams();
  const { identity } = useIdentity();

  /* beforeLoad guarantees an identity; this satisfies the type, not the logic. */
  if (identity === null) return null;

  return (
    <AppLayout lang={lang} identity={identity}>
      <ResumeSessionBanner area="app" lang={lang} />
      <Outlet />
    </AppLayout>
  );
}
